import React, { useState, useEffect } from 'react';
import { websiteAnalyticsService } from '../../services/websiteAnalyticsService';
import { UserSession, WebsiteAnalyticsFilters } from '../../types/websiteAnalytics';
import LineChart from '../Charts/LineChart';
import BarChart from '../Charts/BarChart';

interface SessionAnalyticsProps {
  filters?: WebsiteAnalyticsFilters;
  refreshInterval?: number;
}

interface SessionMetrics {
  totalSessions: number;
  averageDuration: number;
  bounceRate: number;
  pagesPerSession: number;
  newUserSessions: number;
  returningUserSessions: number;
  sessionsByHour: { hour: number; sessions: number }[];
  sessionsByDevice: { device: string; sessions: number; avgDuration: number }[];
  sessionsByDuration: { range: string; sessions: number; percentage: number }[];
}

export const SessionAnalytics: React.FC<SessionAnalyticsProps> = ({
  filters,
  refreshInterval = 300000
}) => {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await websiteAnalyticsService.getUserSessions(filters);
      
      if (response.success && response.data) {
        const sessionsData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any).data || [];
        setSessions(sessionsData);
        setError(null);
      } else {
        setError('Failed to fetch session data');
        setSessions([]);
      }
    } catch (err) {
      setError('Error loading session analytics');
      console.error('Error fetching sessions:', err);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, refreshInterval);
    return () => clearInterval(interval);
  }, [filters, refreshInterval]);

  const calculateSessionMetrics = (): SessionMetrics => {
    if (sessions.length === 0) {
      return {
        totalSessions: 0, averageDuration: 0, bounceRate: 0, pagesPerSession: 0,
        newUserSessions: 0, returningUserSessions: 0, sessionsByHour: [],
        sessionsByDevice: [], sessionsByDuration: []
      };
    }

    const totalSessions = sessions.length;
    const totalDuration = sessions.reduce((sum, session) => sum + (session.duration || 0), 0);
    const averageDuration = totalDuration / totalSessions;
    const bouncedSessions = sessions.filter(session => session.bounced).length;
    const bounceRate = bouncedSessions / totalSessions;
    const totalPageViews = sessions.reduce((sum, session) => sum + session.pageViews, 0);
    const pagesPerSession = totalPageViews / totalSessions;
    const newUserSessions = sessions.filter(session => !session.userId).length;
    const returningUserSessions = totalSessions - newUserSessions;

    const hourlyStats = new Map<number, number>();
    sessions.forEach(session => {
      const hour = new Date(session.startTime).getHours();
      hourlyStats.set(hour, (hourlyStats.get(hour) || 0) + 1);
    });
    const sessionsByHour = Array.from({ length: 24 }, (_, hour) => ({
      hour, sessions: hourlyStats.get(hour) || 0
    }));

    const deviceStats = new Map<string, { sessions: number; totalDuration: number }>();
    sessions.forEach(session => {
      const device = session.deviceType || 'unknown';
      const current = deviceStats.get(device) || { sessions: 0, totalDuration: 0 };
      deviceStats.set(device, {
        sessions: current.sessions + 1,
        totalDuration: current.totalDuration + (session.duration || 0)
      });
    });
    const sessionsByDevice = Array.from(deviceStats.entries()).map(([device, stats]) => ({
      device, sessions: stats.sessions,
      avgDuration: stats.sessions > 0 ? stats.totalDuration / stats.sessions : 0
    }));

    const durationRanges = [
      { min: 0, max: 30, label: '0-30s' },
      { min: 30, max: 60, label: '30s-1m' },
      { min: 60, max: 180, label: '1-3m' },
      { min: 180, max: 300, label: '3-5m' },
      { min: 300, max: 600, label: '5-10m' },
      { min: 600, max: Infinity, label: '10m+' }
    ];
    const sessionsByDuration = durationRanges.map(range => {
      const sessionsInRange = sessions.filter(session => {
        const duration = session.duration || 0;
        return duration >= range.min && duration < range.max;
      }).length;
      return { range: range.label, sessions: sessionsInRange, percentage: (sessionsInRange / totalSessions) * 100 };
    });

    return {
      totalSessions, averageDuration, bounceRate, pagesPerSession,
      newUserSessions, returningUserSessions, sessionsByHour, sessionsByDevice, sessionsByDuration
    };
  };

  if (loading && sessions.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <p className="text-red-600">{error}</p>
        <button onClick={fetchSessions} className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const metrics = calculateSessionMetrics();
  const formatDuration = (seconds: number): string => websiteAnalyticsService.formatDuration(seconds);
  const formatPercentage = (value: number): string => websiteAnalyticsService.formatPercentage(value);

  const hourlySessionsData = metrics.sessionsByHour.map(item => ({
    hour: `${item.hour}:00`, sessions: item.sessions
  }));

  const deviceSessionsData = metrics.sessionsByDevice.map(item => ({
    name: item.device ? item.device.charAt(0).toUpperCase() + item.device.slice(1) : 'Unknown',
    sessions: item.sessions
  }));

  const durationDistributionData = metrics.sessionsByDuration.map(item => ({
    name: item.range, sessions: item.sessions
  }));

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900">Session Analytics</h3>
      </div>

      <div className="p-5">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
            <div className="text-2xl md:text-3xl font-bold text-blue-600">{metrics.totalSessions}</div>
            <div className="text-sm text-gray-600">Total Sessions</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
            <div className="text-2xl md:text-3xl font-bold text-green-600">{formatDuration(metrics.averageDuration)}</div>
            <div className="text-sm text-gray-600">Avg Duration</div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 text-center border border-red-100">
            <div className="text-2xl md:text-3xl font-bold text-red-600">{formatPercentage(metrics.bounceRate)}</div>
            <div className="text-sm text-gray-600">Bounce Rate</div>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
            <div className="text-2xl md:text-3xl font-bold text-purple-600">{metrics.pagesPerSession.toFixed(1)}</div>
            <div className="text-sm text-gray-600">Pages/Session</div>
          </div>
        </div>

        {/* User Type */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-2xl">🆕</div>
            <div>
              <div className="text-sm text-gray-600">New Users</div>
              <div className="text-xl font-bold text-gray-900">
                {metrics.newUserSessions}
                <span className="text-sm font-normal text-gray-500 ml-1">
                  ({metrics.totalSessions > 0 ? ((metrics.newUserSessions / metrics.totalSessions) * 100).toFixed(0) : 0}%)
                </span>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-2xl">🔄</div>
            <div>
              <div className="text-sm text-gray-600">Returning Users</div>
              <div className="text-xl font-bold text-gray-900">
                {metrics.returningUserSessions}
                <span className="text-sm font-normal text-gray-500 ml-1">
                  ({metrics.totalSessions > 0 ? ((metrics.returningUserSessions / metrics.totalSessions) * 100).toFixed(0) : 0}%)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Sessions by Hour Chart */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Sessions by Hour</h4>
          <div className="h-64">
            <LineChart
              data={hourlySessionsData}
              xAxisKey="hour"
              lines={[{ key: 'sessions', color: '#3B82F6', name: 'Sessions' }]}
              height={240}
            />
          </div>
        </div>

        {/* Device & Duration Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Sessions by Device</h4>
            <div className="h-56">
              <BarChart
                data={deviceSessionsData}
                xAxisKey="name"
                bars={[{ key: 'sessions', color: '#10B981', name: 'Sessions' }]}
                height={200}
              />
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Session Duration Distribution</h4>
            <div className="h-56">
              <BarChart
                data={durationDistributionData}
                xAxisKey="name"
                bars={[{ key: 'sessions', color: '#8B5CF6', name: 'Sessions' }]}
                height={200}
              />
            </div>
          </div>
        </div>

        {/* Device Performance Table */}
        <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700">Device Performance</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Device</th>
                  <th className="px-4 py-3 text-right">Sessions</th>
                  <th className="px-4 py-3 text-right hidden sm:table-cell">%</th>
                  <th className="px-4 py-3 text-right">Avg Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {metrics.sessionsByDevice.map((device) => (
                  <tr key={device.device} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span>{websiteAnalyticsService.getDeviceIcon(device.device)}</span>
                        <span className="text-sm font-medium text-gray-900 capitalize">{device.device}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{device.sessions}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right hidden sm:table-cell">
                      {metrics.totalSessions > 0 ? ((device.sessions / metrics.totalSessions) * 100).toFixed(1) : 0}%
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatDuration(device.avgDuration)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Session Duration Breakdown */}
        <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700">Session Duration Breakdown</h4>
          </div>
          <div className="p-4 grid grid-cols-3 md:grid-cols-6 gap-3">
            {metrics.sessionsByDuration.map((duration) => (
              <div key={duration.range} className="text-center p-3 bg-gray-50 rounded-xl">
                <div className="text-xl font-bold text-purple-600">{duration.sessions}</div>
                <div className="text-xs font-medium text-gray-700">{duration.range}</div>
                <div className="text-xs text-gray-500">{duration.percentage.toFixed(1)}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Sessions Table */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700">Recent Sessions</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Session</th>
                  <th className="px-4 py-3 text-left">Duration</th>
                  <th className="px-4 py-3 text-left hidden sm:table-cell">Pages</th>
                  <th className="px-4 py-3 text-left hidden md:table-cell">Device</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sessions
                  .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
                  .slice(0, 8)
                  .map((session) => (
                    <tr key={session.sessionId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{session.sessionId.substring(0, 8)}...</div>
                        <div className="text-xs text-gray-500">{new Date(session.startTime).toLocaleDateString()}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDuration(session.duration || 0)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 hidden sm:table-cell">{session.pageViews}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex items-center gap-1">
                          <span>{websiteAnalyticsService.getDeviceIcon(session.deviceType)}</span>
                          <span className="text-sm text-gray-600 capitalize">{session.deviceType}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          session.bounced ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {session.bounced ? 'Bounced' : 'Engaged'}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
